import { Users, FileText, Clock, ShieldCheck } from "lucide-react";
import { useT } from "@/context/ContentContext";

/**
 * "O nosso compromisso" — editorial trust strip on light cream.
 * Big serif-italic copper numbers, thin dividers, icon + title inline.
 * Intentionally contrasts with the dark forest blocks that surround it.
 */
const ITEMS = [
    { key: "home.trust.item1", def: "Um interlocutor", icon: Users },
    { key: "home.trust.item2", def: "Orçamento claro", icon: FileText },
    { key: "home.trust.item3", def: "Prazos realistas e acompanhados", icon: Clock },
    { key: "home.trust.item4", def: "Cuidado com o imóvel", icon: ShieldCheck },
];

export default function HomeTrustBar() {
    const t = useT();
    return (
        <section className="py-16 sm:py-24 relative" data-testid="home-trust-bar">
            {/* thin copper rule + slim eyebrow row */}
            <div className="max-w-[1400px] mx-auto px-6 sm:px-10">
                <div className="flex items-center gap-4 mb-10">
                    <div className="h-px flex-1 bg-[hsl(var(--copper))]/40" />
                    <span className="font-mono text-[11px] tracking-[0.2em] uppercase text-[hsl(var(--copper))]">
                        {t("home.trust.eyebrow", "O nosso compromisso")}
                    </span>
                    <div className="h-px flex-1 bg-[hsl(var(--copper))]/40" />
                </div>

                {/* Large centred editorial headline */}
                <h2 className="font-display text-center text-3xl sm:text-5xl lg:text-6xl font-bold text-[hsl(var(--forest))] tracking-tight leading-[1.02] max-w-4xl mx-auto">
                    {t("home.trust.title", "Quatro promessas.")}{" "}
                    <span className="font-serif-italic text-[hsl(var(--copper))]">{t("home.trust.title_accent", "Um método")}</span>
                    <span className="text-[hsl(var(--copper))]">.</span>
                </h2>
                <p className="text-center text-[hsl(var(--graphite))]/70 text-lg mt-6 max-w-2xl mx-auto">
                    {t("home.trust.subtitle", "As bases não-negociáveis de qualquer obra que fazemos.")}
                </p>

                {/* Editorial 4-column strip with vertical dividers */}
                <div className="mt-16 grid grid-cols-2 lg:grid-cols-4 lg:divide-x lg:divide-[hsl(var(--forest))]/12">
                    {ITEMS.map((it, i) => (
                        <div
                            key={it.key}
                            data-testid={`trust-${it.key.split(".").pop()}`}
                            className="group relative px-4 sm:px-8 py-8 border-b border-[hsl(var(--forest))]/12 lg:border-b-0"
                        >
                            {/* Oversized italic serif number as backdrop */}
                            <span
                                aria-hidden
                                className="pointer-events-none absolute -top-4 right-2 sm:right-4 font-serif-italic text-6xl sm:text-7xl text-[hsl(var(--copper))]/12 leading-none select-none group-hover:text-[hsl(var(--copper))]/25 transition-colors duration-500"
                            >
                                {`0${i + 1}`}
                            </span>

                            {/* Icon in ring */}
                            <div className="relative w-14 h-14 rounded-2xl bg-white shadow-[0_10px_30px_-15px_rgba(24,53,47,0.25)] border border-[hsl(var(--forest))]/10 flex items-center justify-center mb-6 group-hover:-translate-y-0.5 group-hover:border-[hsl(var(--copper))]/40 transition-all duration-300">
                                <it.icon className="w-6 h-6 text-[hsl(var(--forest))] group-hover:text-[hsl(var(--copper))] transition-colors" strokeWidth={1.75} />
                            </div>

                            {/* Bold serif-adjacent title */}
                            <div className="relative font-display text-xl sm:text-2xl font-bold text-[hsl(var(--forest))] leading-tight">
                                {t(it.key, it.def)}
                            </div>

                            {/* Copper hover underline */}
                            <div className="relative mt-3 h-[2px] w-8 bg-[hsl(var(--copper))]/60 group-hover:w-16 transition-all duration-500" />
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}
